//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';

import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import HR from '@components/atoms/hr/HR';
import DateRangePicker from '@components/date-range-picker/DateRangePicker';
import {
  CUSTOM_DATE_RANGE_SEPARATOR,
  DateRangeAlias,
  IAliasedDateRange,
  getAliasedDateRange,
  getAliasedDateRangeFromCustomDateRange,
  getDateRangeAliasDisplayName,
  getStringFormatDateRangeValue,
  isValidCustomDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import { getCustomDateRangePickerAliases } from '@components/date-range-picker/dateRangePickerUtils';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterSection,
  FilterSectionTitle,
} from '@components/media-filters/filterSectionModels';
import DateString, { DATE_STRING_FORMAT } from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { isDefined } from '@utils/filterPredicates';

const DATE_LABEL_FORMAT = 'M/D/YYYY';
const DEFAULT_DATE_RANGE_ALIAS = 'allTime';
const DefaultDateRangeAliases: DateRangeAlias[] = [
  'last24Hours',
  'last7Days',
  'last30Days',
  'last3Months',
  'allTime',
] as const;

/**
 * Validate if string is a supported date range alias.
 * @param {string} dateRange String to be validated
 * @returns {boolean} `true` if string is a supported date range alias
 */
const isDateRangeAlias = (dateRange: string) => {
  return DefaultDateRangeAliases.includes(dateRange as DateRangeAlias);
};

/**
 * Validate if string is a valid date range alias or custom date range.
 * @param {string} dateRange String to be validated
 * @returns {boolean} `true` if string is a valid date range
 */
export const isValidDateRange = (dateRange: string) => {
  return isDateRangeAlias(dateRange) || isValidCustomDateRange(dateRange);
};

/**
 * Get display name of a valid custom date range.
 * @param dateRange Valid custom date range
 * @returns {string} Display name of custom date range
 */
const parseCustomDateRangeToDisplayName = (dateRange: string) => {
  return dateRange
    .split(CUSTOM_DATE_RANGE_SEPARATOR)
    .map((date) =>
      DateString.format(
        DateString.fromString(date, DATE_STRING_FORMAT),
        DATE_LABEL_FORMAT,
      ),
    )
    .join(' - ');
};

/**
 * Get aliased date range from valid date range alias or custom date range.
 * @param {string} dateRange Valid date range alias or custom date range
 * @param {TimeZoneId} timeZone User's time zone
 * @returns {IAliasedDateRange} Aliased date range object
 */
const parseDateRange = (
  dateRange: string,
  timeZone: TimeZoneId,
): IAliasedDateRange => {
  return isDateRangeAlias(dateRange)
    ? getAliasedDateRange(dateRange as DateRangeAlias, timeZone)
    : getAliasedDateRangeFromCustomDateRange(dateRange, timeZone);
};

/**
 * Get label of a valid date range alias or custom date range.
 * @param {string} dateRange Valid date range alias or custom date range
 * @returns {string} Label for date range filter
 */
export const getDateRangeLabel = (dateRange: string) => {
  return isDateRangeAlias(dateRange)
    ? getDateRangeAliasDisplayName(dateRange as DateRangeAlias)
    : parseCustomDateRangeToDisplayName(dateRange);
};

export const DEFAULT_DATE_RANGE_FILTER_SECTION: FilterSection<string> = {
  options: DefaultDateRangeAliases.map((alias) => ({
    isSelected: false,
    label: getDateRangeAliasDisplayName(alias),
    value: alias,
  })),
  paramKey: 'dateRange',
};

export interface DateRangeFilterProps {
  initialIsCollapsed?: boolean;
  section: FilterSection<string> | undefined;
  onChange: (section: FilterSection<string>) => void;
}

export const DateRangeFilter = ({
  initialIsCollapsed,
  onChange,
  section = DEFAULT_DATE_RANGE_FILTER_SECTION,
}: DateRangeFilterProps) => {
  const userTimeZone = useSelector(getUserTimeZone);

  const initialDateRange = section.options.find(
    ({ isSelected }) => isSelected,
  )?.value;

  const selectedDateRange = isDefined(initialDateRange)
    ? parseDateRange(initialDateRange, userTimeZone)
    : undefined;

  const dateRange =
    selectedDateRange ??
    getAliasedDateRange(DEFAULT_DATE_RANGE_ALIAS, userTimeZone);

  const handleChange = (dateRange: IAliasedDateRange) => {
    const dateRangeString =
      getStringFormatDateRangeValue(dateRange) ?? DEFAULT_DATE_RANGE_ALIAS;

    const updatedSection = structuredClone(DEFAULT_DATE_RANGE_FILTER_SECTION);

    // Clear date range filter value when "All Time" is selected.
    if (dateRangeString === 'allTime') {
      onChange(updatedSection);
      return;
    }

    const option = updatedSection.options.find(
      ({ value }) => value === dateRangeString,
    );

    if (isDefined(option)) {
      option.isSelected = true;
    } else {
      updatedSection.options.push({
        isSelected: true,
        label: getDateRangeLabel(dateRangeString),
        value: dateRangeString,
      });
    }

    onChange(updatedSection);
  };

  return (
    <>
      <CollapsibleSection
        isCollapsed={initialIsCollapsed}
        rightComponent={
          <HelpIcon
            helpText='Filter based on post date (for social content) or upload date (for directly uploaded content) using a custom date range or rolling date ranges.'
            placement='left'
          />
        }
        title={FilterSectionTitle.dateRange}
      >
        <DateRangePicker
          aliases={getCustomDateRangePickerAliases(
            userTimeZone,
            DefaultDateRangeAliases,
          )}
          appendTarget='body'
          maxDate={DateString.getToday(userTimeZone)}
          onDateRangeChanged={handleChange}
          placement='left'
          timeZone={userTimeZone}
          value={dateRange}
        />
      </CollapsibleSection>
      <HR />
    </>
  );
};
